/* ═══════════════════════════════════════════════════════════════
   REVIEW — Finish is idempotent across reloads.

   Finish writes the document FIRST, then records the review on the
   server. If the server call fails (or the window reloads in between),
   a second Finish must not plan the same changes onto a document that
   already holds them. So before the server call we persist
   `sync.applied:<rowId>` = { hash of the document as Finish left it,
   counts }. On Finish, if that marker matches the current document,
   the editor apply is skipped and only the server call repeats.
   Pure — the Prefs read/write lives in syncAdapter.ts.
   ═══════════════════════════════════════════════════════════════ */

import { planReview, type Change, type Decision, type ReviewPlan } from '../../shared/sync';
import type { ContentBlock } from '../../shared/types';

export interface AppliedMarker {
  hash: string;
  applied: number;
  rejected: number;
}

/** FNV-1a over the canonical JSON of the blocks — stable, cheap, local. */
export function hashBlocks(blocks: ContentBlock[]): string {
  const s = JSON.stringify(blocks);
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return `${s.length}:${h.toString(16)}`;
}

export interface FinishPrep {
  plan: ReviewPlan;
  /** True = the document already holds this Finish; only the server call runs. */
  skipApply: boolean;
}

export function prepareFinish(
  current: ContentBlock[],
  changes: Change[],
  decisions: Record<string, Decision>,
  marker: AppliedMarker | null
): FinishPrep {
  if (marker && marker.hash === hashBlocks(current)) {
    return {
      plan: { blocks: current, applied: marker.applied, rejected: marker.rejected },
      skipApply: true,
    };
  }
  return { plan: planReview(current, changes, decisions), skipApply: false };
}

/** A server reply meaning "this row was already resolved" is a success. */
export function isAlreadyResolved(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const e = err as { status?: number; code?: string; message?: string };
  if (e.status === 409) return true;
  if (e.code && /already[_ -]?resolved/i.test(e.code)) return true;
  return !!e.message && /already resolved/i.test(e.message);
}

/** Why Finish must not write right now, or null when it may. */
export function finishBlocker(editorReady: boolean, isLiveDocument: boolean): string | null {
  if (!editorReady) return 'The document is still opening. Wait a moment, then Finish again.';
  if (!isLiveDocument)
    return 'Go back to the live document to finish — a snapshot is open on the page.';
  return null;
}
